/* ══════════════════════════════════════════════════════════════
   selectSymptoms.components.jsx
   Pure sub-components: BottomPanel, SidePanel, LocationCard, BodyPartPill
══════════════════════════════════════════════════════════════ */

import styles from "./SelectSymptoms.module.css";



const normalizeSymptoms = arr =>
  arr.map(s => typeof s === "string" ? s : s.name);

/* ── Shared chip ─────────────────────────────────────────────── */
function Chip({ label, selected, onToggle, icon }) {
  return (
    <button
      onClick={() => onToggle(label)}
      className={`${styles.sssChip} ${selected ? styles.sssChipOn : ""}`}
    >
      {selected
        ? <span className={styles.sssTick}>✓</span>
        : icon && <span className={styles.sssChipIcon}>{icon}</span>
      }
      {label}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════
   BOTTOM PANEL  — slides up, used for zone click + view-all mobile
══════════════════════════════════════════════════════════════ */
export function BottomPanel({ data, selected, onToggle, onClose }) {
  if (!data) return null;
  const { label, sub, symptoms } = data;
  const inZone = selected.filter(s => symptoms.includes(s.label));

  return (
    <div className={styles.sssBottomOverlay} onClick={onClose}>
      <div className={styles.sssBottomPanel} onClick={e => e.stopPropagation()}>
        <div className={styles.sssBottomPanelHandle} />

        <div className={styles.sssBottomPanelHead}>
          <div>
            <p className={styles.sssBottomPanelTitle}>{label}</p>
            {sub && <p className={styles.sssBottomPanelSub}>{sub}</p>}
          </div>
          <button className={styles.sssBottomPanelClose} onClick={onClose}>×</button>
        </div>

        <div className={styles.sssBottomPanelChips}>
          {/* {symptoms.map(sym => (
            <Chip
              key={sym}
              label={sym}
              selected={selected.some(s => s.label === sym)}
              onToggle={onToggle}
            />
          ))} */}
          {symptoms.map(sym => {
            const label = typeof sym === "string" ? sym : sym.name;

            return (
              <Chip
                key={label}
                label={label}
                selected={selected.some(s => s.label === label)}
                onToggle={onToggle}
              />
            );
          })}
        </div>

        {inZone.length > 0 && (
          <div className={styles.sssBottomPanelSel}>
            <span className={styles.sssBottomPanelSelLabel}>Selected:</span>
            {inZone.map(s => (
              <span key={s.id} className={styles.sssBottomPanelSelChip}>{s.label}</span>
            ))}
          </div>
        )}

        <button className={styles.sssBottomPanelDone} onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SIDE PANEL  — right column on desktop (View All)
══════════════════════════════════════════════════════════════ */
export function SidePanel({ cat, selected, onToggle, onClose }) {
  if (!cat) return null;
  return (
    <div className={styles.sssSidePanel}>
      <div className={styles.sssSidePanelHead}>
        <div className={styles.sssSidePanelTitle}>
          <span>{cat.icon}</span>
          <span>{cat.label}</span>
        </div>
        <button className={styles.sssSidePanelClose} onClick={onClose}>×</button>
      </div>

      <p className={styles.sssSidePanelSub}>Select all that apply</p>

      <div className={styles.sssSidePanelChips}>
        {/* {cat.symptoms?.map(sym => (
          <Chip
            key={sym}
            label={sym}
            selected={selected.some(s => s?.label === sym || s?.name === sym )}
            onToggle={onToggle}
          />
        ))} */}

        {cat.symptoms?.map(sym => {
            const label = typeof sym === "string" ? sym : sym.name;

            return (
              <Chip
                key={label}
                label={label}
                selected={selected.some(s => s.label === label)}
                onToggle={onToggle}
              />
            );
          })}
      </div>

      <button className={styles.sssSidePanelDone} onClick={onClose}>Done</button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   LOCATION CARD  — 2×4 grid card
══════════════════════════════════════════════════════════════ */
export function LocationCard({ cat, selected, onViewAll }) {
  return (
    <button
      className={styles.sssLocCard}
      style={{ "--card-bg": cat.color }}
      onClick={() => onViewAll(cat)}
    >
      <div className={styles.sssLocCardIcon} style={{ color: cat.iconColor }}>
        {cat.icon}
      </div>
      <div className={styles.sssLocCardBody}>
        <p className={styles.sssLocCardTitle}>{cat.label}</p>
        <p className={styles.sssLocCardSub}>{cat.sub}</p>
      </div>
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════
   BODY PART PILL  — Advanced tab
══════════════════════════════════════════════════════════════ */
export function BodyPartPill({ part, active, onClick }) {
  return (
    <button
      className={`${styles.sssBodyPill} ${styles.sssBodyPillMobile} ${active ? styles.sssBodyPillOn : ""}`}
      onClick={() => onClick(part.id)}
    >
      <span className={styles.sssBodyPillIcon}>{part.icon}</span>
      <span className={styles.sssBodyPillLabel}>{part.label}</span>
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════
   VOICE STATUS STRIP  — reusable for both mobile/desktop placements
══════════════════════════════════════════════════════════════ */
export function VoiceStatus({ isListening, liveText, voiceBanner, micError, styles: s }) {
  return (
    <>
      {isListening && (
        <>
          <div className={s.sssListenStrip}>
            <span className={s.sssListenDot} />
            <span>Listening… speak your symptoms</span>
          </div>
          {liveText && <span className={s.sssListenLive}>"{liveText}"</span>}
        </>
      )}

      {/* {voiceBanner.length > 0 && (
        <div className={s.sssVoiceBanner}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
          </svg>
          <span>Added via voice:</span>
          {voiceBanner.map(c => (
            <span key={c} className={s.sssVoiceBadge}>{c}</span>
          ))}
        </div>
      )} */}

      {micError && <p className={s.sssMicErr}>{micError}</p>}
    </>
  );
}

function VoiceStatusBox({ isListening, liveText }) {
  if (!isListening) return null;

  return (
    <div className={styles.voiceBox}>
      <span className={styles.voiceDot}></span>
      <span className={styles.voiceText}>
        {liveText || "Listening… speak your symptoms"}
      </span>
    </div>
  );
}